/**
 * TrackingHealthBanner — amber warning that appears above the shipment table
 * when more than 50% of recent tracking runs have failed.
 *
 * Polls GET /api/terminal-integration/jobs/health on mount + every 60s, and
 * surfaces the failing terminals with a one-click link to /settings/terminals
 * so the admin can patch credentials immediately.
 *
 * Auto-hides when the company is healthy (ok: true), has no runs yet, or the
 * user dismisses it for the session via the local 'tracking-banner-dismissed'
 * sessionStorage key.
 */
import React, { useEffect, useState } from 'react';
import { AlertTriangle, X, KeyRound } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const POLL_INTERVAL_MS = 60_000;
const SESSION_KEY = 'tracking-banner-dismissed';

export const TrackingHealthBanner = ({ api }) => {
  const [health, setHealth] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return sessionStorage.getItem(SESSION_KEY) === '1';
  });

  useEffect(() => {
    if (!api) return undefined;
    let cancelled = false;
    const fetchHealth = () => {
      api.get('/terminal-integration/jobs/health')
        .then((res) => { if (!cancelled) setHealth(res.data); })
        .catch(() => { /* silent — banner is best-effort */ });
    };
    fetchHealth();
    const id = setInterval(fetchHealth, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [api]);

  if (dismissed || !health || health.ok || health.reason === 'no_runs') return null;

  const handleDismiss = () => {
    sessionStorage.setItem(SESSION_KEY, '1');
    setDismissed(true);
  };

  const offenders = (health.problem_terminals || []).slice(0, 3);
  const pct = Math.round((health.failure_rate || 0) * 100);

  return (
    <div
      className="mb-4 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 flex items-start gap-3"
      role="alert"
      data-testid="tracking-health-banner"
    >
      <AlertTriangle className="h-5 w-5 text-amber-300 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-semibold text-amber-100">
          {pct}% of recent availability checks failed
        </h3>
        <p className="text-xs text-amber-200/90 mt-1">
          {health.failed_runs} of the last {health.total_runs} scheduler runs failed
          — usually because of missing or expired terminal credentials.
          {offenders.length > 0 && (
            <span> Most-affected terminals: <strong className="font-medium">
              {offenders.map((o) => o.name).join(', ')}
            </strong>.</span>
          )}
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Button
            asChild
            size="sm"
            variant="outline"
            className="border-amber-400/60 text-amber-200 hover:bg-amber-500/20 h-7 px-3 text-xs"
            data-testid="tracking-banner-fix-creds-btn"
          >
            <Link to="/settings/terminals">
              <KeyRound className="h-3 w-3 mr-1" />
              Fix terminal credentials
            </Link>
          </Button>
        </div>
      </div>
      <button
        onClick={handleDismiss}
        className="text-amber-300/80 hover:text-amber-100 p-1 -mr-1 -mt-1"
        title="Dismiss for this session"
        data-testid="tracking-banner-dismiss-btn"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export default TrackingHealthBanner;
